import {UrlParamsType} from '@optimaxdev/utils/dist/types/libs/url';

import {apiConfig} from 'libs/api/api';
import 'url-search-params-polyfill';

export type MapType<V> = {[key: string]: V};

/**
 * Get boolean after checking encoded params
 * @param {string} str - url search params
 * @returns {boolean} - is str has encoded char
 */
export const hasEncodedCharacters = (str: string): boolean => {
    const encodedRegex = /%[0-9A-Fa-f]{2}/g;
    return encodedRegex.test(str);
};
/**
 * Parse url params and return object
 * @param {string} routerSearchParam - router search param
 * @return {object} - url params
 */
export const getUrlParams = (routerSearchParam?: string): MapType<Array<string>> => {
    const paramsStr =
        typeof routerSearchParam === 'undefined' ? window.location.search : routerSearchParam;
    const getParams: string[] = hasEncodedCharacters(paramsStr)
        ? decodeURI(paramsStr).slice(1).split('&')
        : paramsStr.slice(1).split('&');
    const params: {[key: string]: string[]} = {};

    getParams.forEach((item: string) => {
        const arr = item.split('=');
        if (arr.length < 2) {
            return;
        }
        params[arr[0]] = arr[1].split(',');
    });
    return params;
};

/**
 * Get url search string
 * @param {object} params - url search params
 * @returns {string} - url search string
 */
const createUrlString = (params: UrlParamsType | {[key: string]: string | number}): string =>
    Object.entries(params)
        .reduce(
            (searchString, pair) =>
                String(pair[1]) === '' ? searchString : `${searchString}&${pair[0]}=${pair[1]}`,
            '',
        )
        .slice(1);

/**
 * Update url
 *
 * @param {object} params - object with params
 */
export const updateUrlParams = (params: UrlParamsType) => {
    if (__NODEJS__) return;
    const {hash, origin, pathname} = window.location;
    const newUrl = `${origin}${pathname}?${createUrlString(params)}${hash}`;
    window.history.replaceState(window.history.state, document.title, newUrl);
};

/**
 * Parse url params and return object
 * @param {string} item Current name
 * @param {string} routerUrlParams - router url params
 * @return {string} - url param
 */
export const getUrlParameter = (item: string, routerUrlParams?: string): string => {
    const name = item.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${name}=([^&#]*)`);
    const results = regex.exec(
        typeof routerUrlParams === 'undefined' ? window.location.search : routerUrlParams,
    );
    if (results) {
        const isEncoded = hasEncodedCharacters(results[1]);
        if (isEncoded) {
            return decodeURIComponent(results[1].replace(/\+/g, ' '));
        }
        return results[1].replace(/\+/g, ' ');
    }
    return '';
};

/**
 * Delete param by name from the URL and history about him if it needs
 * @param {string} param - name of param
 * @param {boolean} withHistory - delete history
 * polyfill shold be removed when support of IE will be ended
 */
export function deleteParam(param = '', withHistory?: boolean) {
    if (__NODEJS__) return;
    const url = new URLSearchParams(window.location.search);
    url.delete(param);
    const hasEmptyParams = Boolean(url.toString());
    const modifiedUrl = hasEmptyParams
        ? `${window.location.pathname}?${url.toString()}`
        : window.location.pathname;
    window.history.replaceState({}, document.title, modifiedUrl);

    if (withHistory) window.history.pushState({}, document.title, modifiedUrl);
}

/**
 * Isomorphic get url query param value
 * @param {string} param to get value of
 */
export function getUrlSearchParam(param: string) {
    const routerSearchValue: string = apiConfig.hostStore.getState().router.location.search;
    const valueOfParam: string = getUrlParameter(`${param}`, routerSearchValue);
    return valueOfParam;
}
